import React, { Fragment } from 'react';
import type { ChildrenProp, OrientationProp } from '../common-types';
import { Orientation } from '../common-types';
import { HBox, VBox } from '../layout';

export type Steps = {
  /**
   * Header of the step, can be empty if children contain their own header
   */
  label?: string;
  /**
   * ID of step, used as key for each step
   */
  value: string;
  /**
   * Shown underneath the label as short explanation
   */
  subTitle?: string;
} & ChildrenProp;
/**
 * Props for {@link Step}
 */
export type StepProps = {
  /**
   * Step is activated, styling will be different, will be set automatically
   */
  active?: boolean;
  /**
   * Current child of the stepper
   */
  index: number;
  /**
   * Total number of steps
   */
  steps: number;
  /**
   * Orientation; eiter horizontal or vertical
   */
  alignment: Orientation;
  /**
   * Position of the label.
   */
  labelPosition: 'normal' | 'reversed' | 'below';
} & Steps;

/**
 * Props for {@link Stepper}
 * */
export type StepperProps = {
  /**
   * {@link Steps} Array to build the stepper
   */
  steps: Steps[];
  /**
   * Value of active step from {@link steps}, if set to null, none is active and all steps are gray
   */
  current: string | null;
  /**
   * Position of the label.
   */
  labelPosition?: 'normal' | 'reversed' | 'below';
  /**
   * Use shui colors (orange/white dots instead of green/turquoise)
   */
  shuiColors?: boolean;
  /**
   * Condensed style, meaning smaller dots and less space between them
   */
  condensed: boolean;
} & OrientationProp;

/**
 * Item component for Stepper component.
 *
 * @param index
 * @param alignment
 * @param children
 * @param label step label
 * @param active if the step is active
 * @param steps
 * @param subTitle
 * @param labelPosition
 * @param props the {@link StepProps}
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function Step({
  active,
  alignment,
  children,
  label,
  steps,
  index,
  subTitle,
  labelPosition,
  ...props
}: Readonly<StepProps>): React.JSX.Element {
  const className = ['step'];
  if (active) {
    className.push('active');
  }
  if (active === undefined) {
    className.push('timeline');
  }
  if (index === 0) {
    className.push('first');
  } else if (index === steps - 1) {
    className.push('last');
  }
  // add inner lines only for vertical layout and horizontal when label is below:
  const extendLinesCloseToDot =
    alignment === Orientation.VERTICAL ||
    (alignment === Orientation.HORIZONTAL && labelPosition === 'below');

  return (
    <div {...props} className={className.join(' ')}>
      {index !== 0 && <div className="line" />}
      <HBox className="step-container">
        <VBox className="step-content">
          {label && (
            <VBox className="stepper-label">
              {label}
              {subTitle && <div className="subTitle">{subTitle}</div>}
            </VBox>
          )}
          {children}
        </VBox>
        <div className="step-decoration">
          {extendLinesCloseToDot && <div className="line" />}
          <div className="dot" />
          {extendLinesCloseToDot && <div className="line" />}
        </div>
      </HBox>
      {index !== steps - 1 && <div className="line" />}
    </div>
  );
}

/**
 * Component for displaying separated steps.
 *
 * @param orientation the {@link Orientation}
 * @param shuiColors
 * @param condensed
 * @param labelPosition
 * @param children the children
 * @param steps steps to show in the stepper
 * @param current currently active step
 * @param props leftover {@link StepperProps}
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function Stepper({
  orientation = Orientation.VERTICAL,
  labelPosition = 'normal',
  steps,
  current,
  shuiColors = true,
  condensed = false,
  ...props
}: Readonly<StepperProps>): React.JSX.Element {
  const classNames = ['ehs-stepper', orientation];
  if (labelPosition === 'reversed') {
    classNames.push('stepper-label-reversed');
  }
  if (labelPosition === 'below') {
    classNames.push('stepper-label-below');
  }
  if (shuiColors) {
    classNames.push('shui-colors');
  }
  if (condensed) {
    classNames.push('condensed');
  }

  return (
    <div {...props} className={classNames.join(' ')}>
      {steps.map((step: Steps, index: number) => (
        <Fragment key={step.value}>
          <Step
            {...step}
            active={current ? current === step.value : undefined}
            index={index}
            steps={steps.length}
            alignment={orientation}
            labelPosition={labelPosition}
          >
            {step.children}
          </Step>
        </Fragment>
      ))}
    </div>
  );
}
